import { all, call, fork, put, select, takeEvery } from "redux-saga/effects";
import { actions, getters } from "./model";
import {
  getErrorMessage,
  getMaxLengthFormatted,
  getShowMaxLength
} from "./selectors";
import {
  eventId as getEventId,
  eventDetails as getEventDetails
} from "redux/modules/event/selectors";
import { showSnackbar } from "redux/modules/snackbar/actions";
import { getCredentials } from "redux/modules/user/selectors";
import { orgId as getOrgId } from "redux/modules/organization/selectors";
import { registerError } from "redux/modules/errors/actions";
import { hideModal } from "redux/modules/modal/actions";
import { apiCall } from "App/Data/sagas";
import Api from "./api";
import { getFields as refreshFormEditorFieldsList } from "redux/modules/modules/fields/actions";

import * as R from "ramda";

const getParams = function*() {
  const credentials = yield select(getCredentials);
  const orgId = yield select(getOrgId);
  const eventId = yield select(getEventId);

  return {
    orgId,
    eventId,
    credentials
  };
};

const getIds = function*() {
  const orgId = yield select(getters.orgId);
  const eventId = yield select(getters.eventId);
  const moduleId = yield select(getters.moduleId);
  const fieldId = yield select(getters.fieldId);

  return {
    eventId,
    moduleId,
    orgId,
    fieldId
  };
};

const getFieldsForModal = function*(props = {}) {
  try {
    const { moduleId: storedModuleId } = yield call(getIds);
    const { orgId: storedOrgId, eventId: storedEventId } = yield call(
      getParams
    );
    const moduleId = R.propOr(storedModuleId, "moduleId", props);
    const eventId = R.propOr(storedEventId, "eventId", props);
    const orgId = R.propOr(storedOrgId, "orgId", props);
    const {
      payload: { fields }
    } = yield call(apiCall, {
      method: "get",
      url: `/modules/${moduleId}/fields`,
      qs: {
        orgId,
        eventId,
        global: true
      }
    });
    return { fields };
  } catch (error) {
    yield put(
      registerError([
        {
          system: error,
          user: "Error retrieving fields"
        }
      ])
    );
    return { fields: null };
  }
};

const init = function*({ payload: props }) {
  const { orgId: storedOrgId, eventId: storedEventId } = yield call(getParams);
  const { moduleId: storedModuleId } = yield call(getIds);
  const moduleId = R.propOr(storedModuleId, "moduleId", props);
  const eventId =
    props?.eventId && props.eventId.toString().length
      ? props.eventId
      : storedEventId;
  const orgId = props?.orgId && props.orgId.length ? props.orgId : storedOrgId;

  try {
    const { fields } = yield call(getFieldsForModal, {
      moduleId,
      eventId,
      orgId
    });

    let enabledLanguages = [];
    if (eventId) {
      const enabledLanguagesResult = yield call(apiCall, {
        method: "get",
        url: `/event/${eventId}/enabled-languages`
      });
      enabledLanguages = enabledLanguagesResult.payload;

      yield put(
        actions.setEnabledLanguages(enabledLanguages.filter(l => !l.is_default))
      );
    }

    if (!props.fieldId) {
      yield put(
        actions.setInitialData({
          moduleId,
          orgId,
          eventId,
          fields
        })
      );
    } else {
      yield put(
        actions.setInitialEditData({
          moduleId,
          orgId,
          eventId,
          fieldId: R.propOr("", "fieldId", props),
          fields
        })
      );
    }
  } catch (error) {
    yield put(
      registerError([
        {
          system: error,
          user: "An error occurred"
        }
      ])
    );
  } finally {
    yield put(actions.setLoading(false));
  }
};

const getData = function*() {
  const name = yield select(getters.name);
  const language = yield select(getters.language);
  const fieldLevel = yield select(getters.fieldLevel);
  const description = yield select(getters.description);
  const type = yield select(getters.type);
  const dateSettings = yield select(getters.dateSettings);
  const dropdownSettings = yield select(getters.dropdownSettings);
  const dateTimeSettings = yield select(getters.dateTimeSettings);
  const percentSettings = yield select(getters.percentSettings);
  const calculatedNumberSettings = yield select(
    getters.calculatedNumberSettings
  );
  const currencySettings = yield select(getters.currencySettings);
  const lookupSettings = yield select(getters.lookupSettings);
  const referenceSettings = yield select(getters.referenceSettings);
  const phoneSettings = yield select(getters.phoneSettings);
  const textSettings = yield select(getters.textSettings);
  const cateringSettings = yield select(getters.cateringSettings);
  const fileSettings = yield select(getters.fileSettings);
  const maxLength = yield select(getMaxLengthFormatted);
  const showMaxLength = yield select(getShowMaxLength);

  return {
    name,
    type,
    language,
    fieldLevel,
    settings: {
      ...R.propOr({}, type, {
        dropdown: {
          allowMultipleSelect: dropdownSettings.allowMultipleSelect,
          options: dropdownSettings.options.filter(option =>
            option.value?.trim()
          )
        },
        date: dateSettings,
        datetime: dateTimeSettings,
        percent: percentSettings,
        "calculated-number": calculatedNumberSettings,
        currency: currencySettings,
        lookup: lookupSettings,
        reference: referenceSettings,
        phone: phoneSettings,
        text: {
          readOnly: textSettings.readOnly
        },
        catering: cateringSettings,
        file: fileSettings
      }),
      description,
      ...(showMaxLength && maxLength > 0 ? { maxLength } : {})
    }
  };
};

const addField = function*({ addAtPosition, onSave }) {
  try {
    yield put(actions.setError(null));
    yield put(actions.setLoading(true));
    const data = yield call(getData);

    if (typeof addAtPosition !== "undefined") {
      data.addAtPosition = addAtPosition;
    }

    const { orgId, eventId, moduleId } = yield call(getIds);
    const { credentials } = yield call(getParams);
    const fields = yield select(getters.fields);

    const { layout } = yield call(Api.getLayout, {
      credentials,
      moduleId,
      options: {
        orgId,
        eventId
      }
    });

    const newFieldName = R.prop("name", data);
    const newFieldNameExists =
      R.length(
        R.filter(f => R.path(["field", "name"], f) === newFieldName, fields)
      ) > 0;

    if (newFieldNameExists) {
      yield put(showSnackbar({ message: "Field name must be unique" }));
      return;
    }

    const fieldLanguages = yield select(getters.fieldLanguages);

    const { payload: field } = yield call(Api.addField, {
      credentials,
      data: {
        moduleId,
        field: {
          ...data,
          languages: fieldLanguages,
          layoutId: layout.id
        },
        options: {
          orgId,
          eventId
        }
      }
    });

    yield call(onSave, field);
    yield put(showSnackbar({ message: "Field saved" }));
    yield put(hideModal());
  } catch (error) {
    yield put(actions.setError(error.message));
    yield put(
      registerError(
        [
          {
            system: error,
            user: error.message
          }
        ],
        false
      )
    );
  } finally {
    yield put(actions.setLoading(false));
  }
};

const editField = function*({ onSave }) {
  try {
    yield put(actions.setLoading(true));
    const data = yield call(getData);
    const { fieldId, moduleId, eventId } = yield call(getIds);
    const { credentials } = yield call(getParams);
    const description = yield select(getters.description);

    const orgId = yield select(getOrgId);
    const eventDetails = yield select(getEventDetails);
    const orgIdToUse = orgId ? orgId : eventDetails.org_id;

    const fieldLanguages = yield select(getters.fieldLanguages);

    yield call(Api.updateField, {
      credentials,
      data: {
        moduleId,
        field: {
          id: fieldId,
          name: data.name,
          type: data.type,
          fieldLevel: data.fieldLevel,
          languages: fieldLanguages,
          settings: {
            ...data.settings,
            description
          }
        },
        options: {
          orgId: orgIdToUse,
          eventId
        }
      }
    });

    yield call(onSave, data);
    yield put(showSnackbar({ message: "Field saved" }));
    yield put(hideModal());
  } catch (error) {
    yield put(
      registerError([
        {
          system: error,
          user: "An error occurred"
        }
      ])
    );
  } finally {
    yield put(actions.setLoading(false));
  }
};

const handleSave = function*({ payload: { addAtPosition, onSave } }) {
  const error = yield select(getErrorMessage);
  yield put(actions.setError(error));

  if (R.isNil(error)) {
    const { fieldId } = yield call(getIds);

    if (R.isEmpty(fieldId)) {
      yield call(addField, { addAtPosition, onSave });
    } else {
      yield call(editField, { onSave });
    }
  }
};

const handleDelete = function*({ payload: { onSave } }) {
  try {
    yield put(actions.setLoading(true));
    const { fieldId, moduleId, eventId, orgId } = yield call(getIds);
    const { credentials } = yield call(getParams);

    yield call(Api.deleteField, {
      credentials,
      data: {
        moduleId,
        field: {
          id: fieldId
        },
        options: {
          eventId
        }
      }
    });

    yield all([
      put(showSnackbar({ message: "Field deleted" })),
      call(onSave, {}),
      put(
        refreshFormEditorFieldsList({
          moduleId,
          options: { eventId, orgId }
        })
      ),
      put(hideModal())
    ]);
  } catch (error) {
    yield put(
      registerError([
        {
          system: error,
          user: "An error occurred"
        }
      ])
    );
  } finally {
    yield put(actions.setLoading(false));
  }
};

const watchInit = function*() {
  yield takeEvery(actions.init.type, init);
};

const watchSave = function*() {
  yield takeEvery(actions.handleSave.type, handleSave);
};

const watchDelete = function*() {
  yield takeEvery(actions.handleDelete.type, handleDelete);
};

const rootSaga = function*() {
  yield all([fork(watchInit), fork(watchSave), fork(watchDelete)]);
};

export default rootSaga;
